(() => {
  'use strict';
  const EXACT_KEY = 'trmExactVehicleV2';
  const PDP_KEY = 'trmFinderPdpContextV11';
  const GEOMETRY_KEYS = ['wheel_diameter_in','wheel_width_in','bolt_patterns','center_bore_mm','offset_mm','backspacing_in','wheel_load_rating_lbs','tire_diameter_in','tire_section_width_mm','tire_aspect_ratio','tire_rim_diameter_in','clearance_rules','compatibility_state','geometry_audit_status','geometry_ready','geometry_source_hash'];

  const read = (storage, key) => {
    try { return JSON.parse(storage.getItem(key) || 'null'); } catch (_) { return null; }
  };
  const text = (value) => String(value == null ? '' : value).trim();
  const number = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const truthy = (value) => value === true || value === 'true';
  const draftId = () => `TRM-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
  const vehicleKey = (vehicle) => vehicle?.exactVehicleKey || vehicle?.fitmentKey || vehicle?.key || '';
  const vehicleLabel = (vehicle) => vehicle?.label || [vehicle?.year, vehicle?.make, vehicle?.model, vehicle?.trim].filter(Boolean).join(' ') || vehicleKey(vehicle) || 'No truck selected';
  const currentVehicle = () => {
    const sessionVehicle = read(sessionStorage, EXACT_KEY);
    if (sessionVehicle) return sessionVehicle;
    const legacyVehicle = read(localStorage, EXACT_KEY);
    if (legacyVehicle) sessionStorage.setItem(EXACT_KEY, JSON.stringify(legacyVehicle));
    return legacyVehicle;
  };

  function selectedPdpVariant(context) {
    if (!context || !Array.isArray(context.variants)) return null;
    return context.variants.find((variant) => String(variant.id) === String(context.selectedVariantId)) || context.variants[0] || null;
  }

  function consistencyCheck(geometry) {
    const width = number(geometry?.wheel_width_in);
    const offset = number(geometry?.offset_mm);
    const publishedBackspacing = number(geometry?.backspacing_in);
    if (width == null || offset == null || publishedBackspacing == null) return { state: 'MISSING', calculated: null, delta: null };
    const calculated = ((width + 1) / 2) + (offset / 25.4);
    const delta = Math.abs(calculated - publishedBackspacing);
    return { state: delta > 0.15 ? 'CONFLICT' : 'CONSISTENT', calculated, delta };
  }

  function validateWheel(contract, vehicle, pdp, fields) {
    const blockers = [];
    const variant = selectedPdpVariant(pdp);
    const geometry = variant?.geometry || {};
    if (!vehicleKey(vehicle)) blockers.push('missing_exact_vehicle_key');
    if (!pdp || pdp.finderKey !== 'WHEEL_TIRE' || !variant?.sku) blockers.push('missing_exact_wheel_or_tire_sku');
    GEOMETRY_KEYS.forEach((key) => {
      if (geometry[key] == null || geometry[key] === '' || (Array.isArray(geometry[key]) && !geometry[key].length)) blockers.push(`missing_${key}`);
    });
    if (!truthy(geometry.geometry_ready)) blockers.push('geometry_ready_not_true');
    if (text(geometry.geometry_audit_status).toUpperCase() !== 'VERIFIED') blockers.push('geometry_audit_status_not_verified');
    if (!text(fields.front_lift_in) || !text(fields.rear_lift_in)) blockers.push('installed_lift_not_exact');
    if (!text(fields.suspension_kit_mpn)) blockers.push('suspension_kit_mpn_missing');
    const math = consistencyCheck(geometry);
    if (math.state === 'CONFLICT') {
      blockers.push(`offset_backspacing_source_conflict_delta_${math.delta.toFixed(3)}in`);
      return {
        state: 'SOURCE_CONFLICT',
        title: 'This setup needs a quick spec check',
        copy: 'Two manufacturer measurements do not fully agree. Tex can help confirm the exact wheel setup before you order.',
        blockers
      };
    }
    if (math.state === 'MISSING') blockers.push('offset_backspacing_relationship_unverifiable');
    if (!truthy(contract?.v10Policy?.ready)) blockers.push('v10_policy_not_enforceable');
    blockers.push('signed_server_clearance_decision_missing');
    blockers.push('server_cart_validation_not_deployed');
    return {
      state: 'CONDITIONAL_UNRESOLVED',
      title: 'Let’s confirm your wheel setup',
      copy: 'Your wheel is available, but tire size, suspension and clearance can change the final fit. Ask Tex for a quick setup check before ordering.',
      blockers: [...new Set(blockers)]
    };
  }

  function validateGeneric(finderKey, contract, vehicle, pdp, fields) {
    const blockers = [];
    if (!vehicleKey(vehicle)) blockers.push('missing_exact_vehicle_key');
    Object.entries(fields).forEach(([key, value]) => { if (!text(value) && key !== 'installed_changes') blockers.push(`missing_${key}`); });
    const expectedPdp = pdp?.finderKey === finderKey;
    if (!expectedPdp) blockers.push('exact_product_sku_not_loaded');
    if (finderKey === 'BED_COVER') {
      return validateBedCover(contract, vehicle, pdp, fields);
    } else if (finderKey === 'LIGHTING') {
      blockers.push('verified_light_mount_harness_bom_missing');
    } else if (finderKey === 'RECOVERY') {
      const loaded = number(fields.loaded_weight_lbs);
      if (loaded != null) blockers.push(`minimum_line_pull_${Math.ceil((loaded * 1.5) / 500) * 500}_lb_subject_to_verified_mount`);
      blockers.push('verified_mount_and_electrical_rule_missing');
    }
    blockers.push('signed_server_decision_missing');
    return {
      state: 'CONDITIONAL_UNRESOLVED',
      title: 'Your setup is saved for this session',
      copy: 'Tex can help confirm the remaining details before you order.',
      blockers: [...new Set(blockers)]
    };
  }

  function requestedCoverClass(value) {
    const classes = {
      hard_tri_fold: { security: 'hard', function: 'tri-fold' },
      soft_roll_up: { security: 'soft', function: 'roll-up' },
      hard_roll_up: { security: 'hard', function: 'roll-up' },
      retractable: { security: null, function: 'retractable' }
    };
    return classes[text(value)] || null;
  }

  function validateBedCover(contract, vehicle, pdp, fields) {
    const blockers = [];
    const key = vehicleKey(vehicle);
    const requestedLength = number(fields.bed_length_in);
    const requestedClass = requestedCoverClass(fields.cover_style);
    const catalog = Array.isArray(window.TRM_RCBED_COVERS_V13) ? window.TRM_RCBED_COVERS_V13 : [];
    if (!key) blockers.push('missing_exact_vehicle_key');
    if (requestedLength == null) blockers.push('missing_exact_bed_length');
    if (!requestedClass) blockers.push('cover_style_not_normalized_or_heavy_duty_rule_missing');
    if (!catalog.length) blockers.push('v13_rough_country_preview_index_unavailable');

    const matches = catalog.filter((item) => {
      if (!key || requestedLength == null || !requestedClass) return false;
      if (!Array.isArray(item.fitmentKeys) || !item.fitmentKeys.includes(key)) return false;
      if (Math.abs(number(item.bedLengthIn) - requestedLength) > 0.25) return false;
      if (requestedClass.security && item.securityClass !== requestedClass.security) return false;
      return item.functionClass === requestedClass.function;
    });

    if (!matches.length) {
      blockers.push('no_exact_v13_vehicle_bed_length_style_intersection');
      blockers.push('manual_trm_source_review_required');
      return {
        state: 'CONDITIONAL_UNRESOLVED',
        title: 'No exact cover match yet',
        copy: 'Try checking the measured bed length or choose another cover style. Tex can help if your truck has rails, a rack or a toolbox.',
        blockers: [...new Set(blockers)],
        matches: []
      };
    }

    if (text(fields.bed_rail_system) !== 'none') blockers.push('bed_rail_interaction_requires_manual_confirmation');
    if (text(fields.bed_interaction) !== 'none') blockers.push('rack_toolbox_or_hitch_interaction_requires_manual_confirmation');
    if (!truthy(contract?.v10Hub?.ready)) blockers.push('v10_bed_cover_hub_not_finally_certified');
    blockers.push('signed_server_fitment_certificate_pending');
    return {
      state: 'CONDITIONAL_MATCHES_FOUND',
      title: `${matches.length} verified option${matches.length === 1 ? '' : 's'} found`,
      copy: 'These covers match the selected truck, measured bed length and cover style using current manufacturer data.',
      blockers: [...new Set(blockers)],
      matches
    };
  }

  function renderDecision(root, result) {
    const panel = root.querySelector('[data-trm-v11-decision]');
    panel.hidden = false;
    panel.dataset.state = result.state;
    const friendlyState = result.state === 'CONDITIONAL_MATCHES_FOUND' ? 'Verified options' : 'Fitment check';
    root.querySelector('[data-trm-v11-state]').textContent = friendlyState;
    root.querySelector('[data-trm-v11-decision-title]').textContent = result.title;
    root.querySelector('[data-trm-v11-decision-copy]').textContent = result.copy;
    const list = root.querySelector('[data-trm-v11-blockers]');
    if (list) { list.replaceChildren(); list.hidden = true; }
    panel.focus({ preventScroll: true });
    renderBedCoverResults(root, result.matches || []);
  }

  function renderBedCoverResults(root, matches) {
    const panel = root.querySelector('[data-trm-v11-results]');
    const grid = root.querySelector('[data-trm-v11-result-grid]');
    if (!panel || !grid) return;
    panel.hidden = !matches.length;
    grid.replaceChildren(...matches.slice(0, 12).map((item) => {
      const card = document.createElement('article');
      card.className = 'trm-v11__result-card';
      const title = document.createElement('h3');
      title.textContent = item.title || `Rough Country ${item.mpn}`;
      const details = document.createElement('p');
      details.textContent = `${item.sku} · ${item.bedLength} · ${item.securityClass} ${item.functionClass}`;
      const link = document.createElement('a');
      link.className = 'trm-v11__secondary';
      link.href = item.url;
      link.textContent = 'Review product';
      card.append(title, details, link);
      return card;
    }));
    const heading = root.querySelector('[data-trm-v11-results-title]');
    if (heading) heading.textContent = `${matches.length} verified candidate${matches.length === 1 ? '' : 's'}`;
  }

  const openTexChat = () => {
    const chat = document.querySelector('shopify-chat');
    if (!chat) return false;
    if (typeof chat.open === 'function') { chat.open(); return true; }
    const runtime = chat.shadowRoot?.querySelector('shopify-agent-runtime');
    const launcher =
      runtime?.shadowRoot?.querySelector('button[part="activator"], button[aria-label="Need help?"]') ||
      chat.shadowRoot?.querySelector('button[part="activator"], button[aria-label="Need help?"]');
    if (!launcher) return false;
    if (launcher.getAttribute('aria-expanded') !== 'true') launcher.click();
    return true;
  };
  const requestTexChat = (attempts = 8) => {
    if (openTexChat() || attempts <= 1) return;
    window.setTimeout(() => requestTexChat(attempts - 1), 250);
  };

  document.querySelectorAll('[data-trm-v11]').forEach((root) => {
    if (root.dataset.booted === 'true') return;
    root.dataset.booted = 'true';
    const contract = JSON.parse(root.querySelector('[data-trm-v11-contract]')?.textContent || '{}');
    const finderKey = root.dataset.finderKey;
    const form = root.querySelector('[data-trm-v11-form]');
    let lastRequest = null;

    const refreshContext = () => {
      const vehicle = currentVehicle();
      const pdp = read(sessionStorage, PDP_KEY);
      root.querySelector('[data-trm-v11-vehicle-label]').textContent = vehicleLabel(vehicle);
      const variant = selectedPdpVariant(pdp);
      root.querySelector('[data-trm-v11-sku-label]').textContent = variant?.sku ? `PDP SKU: ${variant.sku}` : 'No product SKU passed from a PDP';
    };

    root.querySelector('[data-trm-v11-garage]')?.addEventListener('click', (event) => {
      window.dispatchEvent(new CustomEvent('trm:garage-open', { detail: { trigger: event.currentTarget, source: 'finder-v11' } }));
    });
    root.querySelector('[data-trm-v11-tech]')?.addEventListener('click', () => {
      window.dispatchEvent(new CustomEvent('trm:tex-open', { detail: { source: 'finder-v11', finderKey } }));
      requestTexChat();
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const fields = Object.fromEntries(new FormData(form).entries());
      const vehicle = currentVehicle();
      const pdp = read(sessionStorage, PDP_KEY);
      const result = finderKey === 'WHEEL_TIRE'
        ? validateWheel(contract, vehicle, pdp, fields)
        : validateGeneric(finderKey, contract, vehicle, pdp, fields);
      lastRequest = {
        version: 'TRM-PF-2026-08-30-V13', finderKey, state: result.state,
        exactVehicleKey: vehicleKey(vehicle) || null,
        vehicleLabel: vehicleLabel(vehicle),
        pdpContext: pdp || null, inputs: fields, blockers: result.blockers,
        requestedAt: new Date().toISOString()
      };
      sessionStorage.setItem('trmFinderRequestV11', JSON.stringify(lastRequest));
      window.dispatchEvent(new CustomEvent('trm:fitment-request', { detail: lastRequest }));
      renderDecision(root, result);
    });

    const recoveryWeight = form.querySelector('[name="loaded_weight_lbs"]');
    recoveryWeight?.addEventListener('input', () => {
      const output = root.querySelector('[data-trm-v11-recovery-output]');
      const loaded = number(recoveryWeight.value);
      output.textContent = loaded == null ? 'Minimum winch class requires verified loaded weight.' : `Preflight minimum: ${Math.ceil((loaded * 1.5) / 500) * 500} lb. Exact mount and electrical gates still apply.`;
    });
    window.addEventListener('trm:vehicle-changed', refreshContext);
    window.addEventListener('storage', refreshContext);
    refreshContext();
  });
})();
